import { Box, Typography, IconButton, Divider } from '@mui/material';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { createPortal } from 'react-dom';

const MotionBox = motion(Box);

interface HighlightCardProps {
  icon: string;
  title: string;
  events: string[];
  handleOpen?: () => void;
  handleClose?: () => void;
}

const MinCard = ({ icon, title, handleOpen }: HighlightCardProps) => {
  return (
    <MotionBox
      layoutId={title}
      onClick={handleOpen}
      sx={{
        width: { xs: '60%', sm: 150 },
        height: { xs: 100, sm: 150 },
        mb: 2,
        p: 2,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(30px) saturate(200%)',
        WebkitBackdropFilter: 'blur(30px) saturate(200%)',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.04), 0 8px 32px rgba(0,0,0,0.3)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        },
      }}
    >
      <Typography fontSize={28}>{icon}</Typography>
      <Typography fontFamily='serif'>{title}</Typography>
    </MotionBox>
  );
};

const ModalBackdrop = ({ handleClose }: HighlightCardProps) => (
  <MotionBox
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={handleClose}
    sx={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(4px)',
      zIndex: 10,
    }}
  />
);

const MaxCard = ({ icon, title, events, handleClose }: HighlightCardProps) => (
  <MotionBox
    layoutId={title}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25 }}
    sx={{
      position: 'fixed',
      top: { xs: '30%', sm: '40%' },
      left: { xs: '12vw', sm: '35vw' },
      width: { xs: '60vw', sm: '30vw' },
      p: 4,
      borderRadius: '24px',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(30px) saturate(200%)',
      WebkitBackdropFilter: 'blur(30px) saturate(200%)',
      border: '1px solid rgba(255,255,255,0.25)',
      boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
      zIndex: 20,
      color: 'white',
      textAlign: 'center',
    }}
  >
    <IconButton
      onClick={handleClose}
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        color: 'white',
      }}
    >
      <CloseIcon />
    </IconButton>

    <Typography fontSize={40} mb={1}>
      {icon}
    </Typography>
    <Typography variant='h6' fontFamily='serif'>
      {title}
    </Typography>
    {events.map((e, idx) => (
      <Box key={e}>
        <Typography my={1} fontSize={14}>
          {e}
        </Typography>
        {idx < events.length - 1 && <Divider />}
      </Box>
    ))}
  </MotionBox>
);

const HighlightCard = (props: HighlightCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRoot = document.getElementById('modal-root');

  const newProps = {
    ...props,
    handleOpen: () => setIsOpen(true),
    handleClose: () => setIsOpen(false),
  };

  return (
    <LayoutGroup>
      <MinCard {...newProps} />

      {modalRoot &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <>
                <ModalBackdrop {...newProps} />
                <MaxCard {...newProps} />
              </>
            )}
          </AnimatePresence>,
          modalRoot
        )}
    </LayoutGroup>
  );
};

export default HighlightCard;
